<template>
  <tr v-bind:class="{ nextStop: isNextStop }">
    <td>
      <div class="stop-name">
        <span>{{ stop.station }}</span>
        <v-menu location="bottom start" v-if="disruptions.length">
          <template v-slot:activator="{ props }">
            <v-btn
              v-bind="props"
              class="d-sm-none disruption-flag"
              icon="mdi-alert-circle"
              color="error"
              variant="text"
              size="x-small"
              aria-label="Störungen anzeigen"/>
          </template>
          <v-card class="disruption-popup" max-width="280">
            <v-card-text>
              <Disruption :message="message" v-for="message in disruptions" v-bind:key="message.code"/>
            </v-card-text>
          </v-card>
        </v-menu>
      </div>
      <div v-if="platforms.length" class="stop-platform text-caption font-mono">
        Gl.
        <template v-for="(platform, index) in platforms" :key="`${platform.planned}-${platform.current}`">
          <span v-if="index > 0"> / </span>
          <span
            v-if="platform.changed"
            class="platform-changed"
            :title="`Gleiswechsel, geplant Gl. ${platform.planned}`">
            <!-- aria-label on a role-less span is not reliably read; spell it out in hidden text instead. -->
            <span class="d-sr-only">Gleiswechsel: geplant Gleis {{ platform.planned }}, jetzt Gleis {{ platform.current }}</span>
            <span aria-hidden="true"><s>{{ platform.planned }}</s> → <span class="text-warning">{{ platform.current }}</span></span>
          </span>
          <span v-else>{{ platform.current }}</span>
        </template>
      </div>
    </td>
    <td
      v-bind:style="{ color: delayColor(stop.arrival) }"
      v-bind:class="{ cancelled: isCancelled(stop.arrival) }"
      :title="toTime(toPlannedTime(stop.arrival))">
        {{ toTime(toCurrentTime(stop.arrival)) }} <span v-if="!isCancelled(stop.arrival) && toDelay(stop.arrival)">{{ toDelay(stop.arrival) }}</span>
    </td>
    <td
      v-bind:style="{ color: delayColor(stop.departure) }"
      v-bind:class="{ cancelled: isCancelled(stop.departure) }"
      :title="toTime(toPlannedTime(stop.departure))">
        {{ toTime(toCurrentTime(stop.departure)) }} <span v-if="!isCancelled(stop.departure) && toDelay(stop.departure)">{{ toDelay(stop.departure) }}</span>
    </td>
    <td class="d-none d-sm-table-cell" v-if="hasVehicles">
      <VehicleLink class="enum" v-for="vehicle in vehicles" v-bind:key="vehicle" :vehicle="vehicle"/>
    </td>
    <td class="d-none d-sm-table-cell">
      <div>
        <Disruption :message="message" v-for="message in disruptions" v-bind:key="message.code"/>
      </div>
    </td>
    <td class="d-none d-sm-table-cell"><v-icon>{{ trendIcon }}</v-icon></td>
  </tr>
</template>

<script>
import isEqual from 'lodash/isEqual';
import { DateTime } from 'luxon'
import DateTimeMixin from '@/mixins/DateTimeMixin'
import DelayMixin from '@/mixins/DelayMixin'
import Disruption from '@/components/Disruption.vue'
import VehicleLink from '@/components/VehicleLink.vue'

export default {
  name: 'TrainStop',
  components: {
    Disruption,
    VehicleLink,
  },
  mixins: [DateTimeMixin, DelayMixin],
  props: ['stop', 'previousStop', 'hasVehicles'],

  computed: {
    disruptions: function() {
      // Show a message at the first stop that carries it — IRIS attaches a message to every
      // following stop, so anything already present on the previous stop is a repeat. Filtering
      // by timestamp against the previous stop's time (as done before) drops messages that were
      // entered before the train left the previous stop but attached only from a later stop on
      // (e.g. an Umleitung keyed in while the train still dwelled at the previous station).
      var previousMessageKeys = new Set(this.stopMessages(this.previousStop).map(message => `${message.code}@${message.timestamp}`));
      var filteredMessages = this.stopMessages(this.stop).filter(message => !previousMessageKeys.has(`${message.code}@${message.timestamp}`));
      return [...new Map(filteredMessages.map(item => [item.code, item])).values()];
    },

    trendIcon: function() {
      if (this.stop && this.previousStop) {
        var delta = this.delayDelta(this.previousStop, this.stop);
        if (delta <= -10) {
          return "mdi-arrow-down";
        } else if (delta < 0) {
          return "mdi-arrow-bottom-right";
        } else if (delta >= 10) {
          return "mdi-arrow-up";
        } else if (delta > 0) {
          return "mdi-arrow-top-right";
        }
      }
      return null;
    },

    isNextStop: function() {
      if (this.stop) {
        if (this.previousStop) {
          var now = DateTime.local();
          var prevStopDeparture = DateTime.fromISO(this.currentTimeForStop(this.previousStop), { zone: "UTC" });
          var stopDeparture = DateTime.fromISO(this.currentTimeForStop(this.stop), { zone: "UTC" });

          return now > prevStopDeparture && now < stopDeparture;
        } else {
          return DateTime.local() < DateTime.fromISO(this.currentTimeForStop(this.stop), { zone: "UTC" });
        }
      }

      return false;
    },

    vehicles: function() {
      if (!this.previousStop || this.previousStop.vehicles && this.stop.vehicles && !isEqual(this.previousStop.vehicles, this.stop.vehicles) || !this.previousStop.vehicles && this.stop.vehicles) {
        return this.stop.vehicles;
      }

      return null;
    },

    // Arrival and departure platform nearly always agree; show the pair only when
    // they differ. A changed platform (IRIS `cp`) is shown against the planned one.
    // Arrival and departure are deduplicated only when they agree on BOTH the
    // planned and the current platform (and so also on whether it changed) —
    // otherwise each side renders its own entry, e.g. arrival unchanged on 6
    // alongside departure changed from 5 to 6.
    platforms: function() {
      var result = []
      var sides = [this.stop.arrival, this.stop.departure]
      sides.forEach(ad => {
        var platform = this.platformOf(ad)
        if (!platform) return
        var same = result.find(existing => existing.planned === platform.planned && existing.current === platform.current)
        if (!same) {
          result.push(platform)
        }
      })
      return result
    },
  },

  methods: {
    delayColor: function(arrivalOrDeparture) {
      if (arrivalOrDeparture) {
        if (!arrivalOrDeparture.cancelled && arrivalOrDeparture.delayMinutes != null) {
          return this.delayToColor(arrivalOrDeparture.delayMinutes);
        }
      }
    },

    isCancelled: function(arrivalOrDeparture) {
      if (arrivalOrDeparture) {
        return arrivalOrDeparture.cancelled;
      }
    },

    stopMessages: function(stop) {
      if (stop) {
        if (stop.departure) {
          return stop.departure.messages || [];
        } else if (stop.arrival) {
          return stop.arrival.messages || [];
        }
      }

      return [];
    },

    currentTimeForStop: function(stop) {
      if (stop.departure) {
        return this.toCurrentTime(stop.departure);
      } else if (stop.arrival) {
        return this.toCurrentTime(stop.arrival);
      }

      return null;
    },

    toCurrentTime: function(arrivalOrDeparture) {
      if (arrivalOrDeparture) {
        if (arrivalOrDeparture.cancelled) {
          return arrivalOrDeparture.plannedTime;
        } else if (arrivalOrDeparture.currentTime) {
          return arrivalOrDeparture.currentTime;
        } else {
          return arrivalOrDeparture.plannedTime;
        }
      }
    },

    toPlannedTime: function(arrivalOrDeparture) {
      if (arrivalOrDeparture) {
        return arrivalOrDeparture.plannedTime;
      }
    },

    platformOf: function(arrivalOrDeparture) {
      if (!arrivalOrDeparture) return null
      var planned = arrivalOrDeparture.platform || null
      var current = arrivalOrDeparture.changedPlatform || planned
      if (!current) return null
      return {
        planned: planned,
        current: current,
        changed: planned != null && current !== planned,
      }
    },
  },
};
</script>

<style scoped>
/* A flex row rather than a float: a float aligns to the line box it is declared
   on, so beside a name that wraps ("Kassel-Wilhelmshöhe") it sat against the
   first line instead of the cell. Centring is not something a float can do.
   The cost is that the name no longer wraps around the icon — every line is
   shortened by the icon's width, not just the first — which only affects the
   rows that have a disruption at all. */
.stop-name {
  display: flex;
  align-items: center;
  gap: 2px;
}
/* 24px is the WCAG 2.5.8 minimum target size, down from Vuetify's 32px, which
   is wide enough to cost the name another line in a ~130px column.
   `.v-btn--icon` is in the selector to beat Vuetify's own
   `.v-btn--icon.v-btn--density-default`, which the class alone only ties. */
.disruption-flag.v-btn--icon {
  flex: none;
  margin-left: auto;
  width: 24px;
  height: 24px;
}
.disruption-flag :deep(.v-icon) {
  font-size: 16px;
}

/* The menu content is teleported out of this row, but it is declared in this
   template, so the scope attribute still lands on .disruption-popup. The global
   `.enum` separator already runs the reasons together comma-separated; only the
   red needs dropping, since in here the text is the content rather than a flag
   on a row, and the card supplies the colour. */
.disruption-popup :deep(.disruption) {
  color: inherit;
}

/* Secondary line under the station name rather than a column of its own: below
   md the table is already the widest thing on the page. */
.stop-platform {
  color: rgba(var(--v-theme-on-surface), var(--v-medium-emphasis-opacity));
  line-height: 1.2;
}
.platform-changed s {
  opacity: 0.7;
}
</style>
